import { useEffect, useState } from "react";
import { Plus, FileDown, Eye } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import ShowroomFormsList from "../components/ShowroomFormsList";
import { toast } from "sonner";
import NewFormModal, { FORM_LABELS, Field } from "../components/NewFormModal";
import SignaturePad from "../components/SignaturePad";
import { useStatusLabel } from "../components/StatusBadge";

import { tr, lbl } from "../lib/i18n";
function StatusText({ s }) {
  const label = useStatusLabel();
  const out = label(s);
  return out === s ? lbl(s) : out;
}

const RETURN_FIELDS = [["odometer_in", tr("x.odometer_in_km"), "number"], ["fuel_in", tr("x.fuel_level_in"), "text"], ["condition_notes", tr("x.condition_on_return"), "text"]];

export async function downloadFormPdf(f) {
  try {
    const base = ["owner_auth", "sale_contract"].includes(f.form_type) ? "showroom-forms" : "forms";
    const { data } = await api.get(`/${base}/${f.id}/pdf`, { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = `${f.reference}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch (e) { toast.error(formatApiError(e)); }
}

function Sig({ label, src }) {
  return (
    <div><div className="kpi-label mb-1">{label}</div>
      {src ? <img src={src} alt={label} style={{ height: 80, background: "#fff", border: "1px solid var(--divider)" }} /> : <div className="text-xs opacity-60">{tr("x.not_signed")}</div>}
    </div>
  );
}

function FormView({ form, onClose, onChanged, canEdit }) {
  const [ret, setRet] = useState({});
  const [retSig, setRetSig] = useState(null);
  const canReturn = canEdit && form.status === "Active" && form.form_type !== "authorization";
  const complete = async () => {
    if (form.form_type === "inspection" && !retSig) return toast.error(tr("x.employee_signature_at_return_is_required"));
    try {
      await api.post(`/forms/${form.id}/complete-return`, { return: ret, employee_return_signature: retSig });
      toast.success(tr("x.vehicle_returned"));
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const d = form.data || {};
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-start sm:items-center justify-center p-2 sm:p-4 overflow-y-auto" onClick={onClose}>
      <div className="panel w-full max-w-2xl my-4" onClick={(e) => e.stopPropagation()} data-testid="form-view">
        <div className="panel-title flex items-center justify-between">
          <div><div className="kicker">{FORM_LABELS[form.form_type]}</div><div className="text-sm font-medium" data-testid="form-view-ref">{form.reference} · {form.status}</div></div>
          <button className="btn-ghost" onClick={onClose} data-testid="form-view-close">{tr("x.close")}</button>
        </div>
        <div className="panel-body grid gap-4">
          <div className="grid gap-2 text-sm sm:grid-cols-2">
            <div><span className="opacity-60">{tr("x.vehicle_3")} </span>{form.vehicle_label}</div>
            <div><span className="opacity-60">{tr("x.customer_2")} </span>{form.customer_name || "—"}</div>
            {Object.entries(d).filter(([k]) => k !== "return").map(([k, v]) => (
              <div key={k}><span className="opacity-60">{k.replace(/_/g, " ")}: </span>{String(v)}</div>
            ))}
          </div>
          <div className="grid gap-4 sm:grid-cols-3" data-testid="form-view-signatures">
            <Sig label={tr("x.customer")} src={form.customer_signature} />
            {form.form_type !== "testdrive" && <Sig label={`${tr("x.employee")} · ${form.employee_name}`} src={form.employee_signature} />}
            {form.form_type === "inspection" && form.status === "Completed" && <Sig label={`${tr("x.employee_at_return")} · ${form.returned_by_name || ""}`} src={form.employee_return_signature} />}
          </div>
          {canReturn && (
            <div className="grid gap-3" style={{ borderTop: "1px solid var(--divider)", paddingTop: 16 }} data-testid="form-return-section">
              <div className="kicker">{tr("x.return_vehicle")}</div>
              <div className="grid gap-3 sm:grid-cols-3">
                {RETURN_FIELDS.map((def) => <Field key={def[0]} def={def} value={ret[def[0]]} onChange={(v) => setRet((r) => ({ ...r, [def[0]]: v }))} />)}
              </div>
              {form.form_type === "inspection" && <SignaturePad label={tr("x.employee_signature_return")} testid="sig-employee-return" onChange={setRetSig} />}
              <button className="btn-primary" style={{ width: "fit-content" }} onClick={complete} data-testid="form-complete-return">{tr("x.complete_return")}</button>
            </div>
          )}
          <button className="btn-secondary" style={{ width: "fit-content" }} onClick={() => downloadFormPdf(form)} data-testid="form-view-pdf"><FileDown size={14} /> {tr("x.download_pdf")}</button>
        </div>
      </div>
    </div>
  );
}

export default function FormsPage() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [type, setType] = useState("");
  const [creating, setCreating] = useState(false);
  const [viewing, setViewing] = useState(null);
  const load = async () => {
    try { setItems((await api.get("/forms", { params: { form_type: type || undefined } })).data.items || []); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [type]);
  const open = async (f) => {
    try { setViewing((await api.get(`/forms/${f.id}`)).data); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="forms-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div><div className="kicker">{tr("x.signed_paperwork")}</div>
          <h1 className="mt-2" style={{ fontSize: 34, fontWeight: 600, letterSpacing: "-0.02em" }}>{tr("x.forms")}</h1></div>
        {can("forms", "create") && <button className="btn-primary" onClick={() => setCreating(true)} data-testid="new-form-btn"><Plus size={14} /> {tr("x.new_form")}</button>}
      </div>
      <ShowroomFormsList />
      <div className="seg" style={{ width: "fit-content", maxWidth: "100%", overflowX: "auto" }}>
        {[["", tr("x.all")], ...Object.entries(FORM_LABELS)].map(([k, l]) => (
          <button key={k || "all"} className={type === k ? "active" : ""} onClick={() => setType(k)} data-testid={`forms-filter-${k || "all"}`}>{l}</button>
        ))}
      </div>
      <div className="panel overflow-x-auto">
        <table className="w-full dense-table" data-testid="forms-table">
          <thead><tr><th>{tr("x.reference")}</th><th>{tr("x.type")}</th><th>{tr("x.vehicle_2")}</th><th>{tr("x.customer")}</th><th>{tr("x.status")}</th><th>{tr("x.date")}</th><th /></tr></thead>
          <tbody>
            {items.map((f) => (
              <tr key={f.id} data-testid={`form-row-${f.id}`}>
                <td className="font-medium">{f.reference}</td><td>{FORM_LABELS[f.form_type]}</td><td>{f.vehicle_label}</td>
                <td>{f.customer_name || "—"}</td><td><StatusText s={f.status} /></td><td>{f.created_at?.slice(0, 10)}</td>
                <td className="whitespace-nowrap">
                  <button className="btn-ghost" onClick={() => open(f)} aria-label={tr("x.view")} data-testid={`form-open-${f.id}`}><Eye size={14} /></button>
                  <button className="btn-ghost" onClick={() => downloadFormPdf(f)} aria-label="PDF" data-testid={`form-pdf-${f.id}`}><FileDown size={14} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="text-center py-8 opacity-60">{tr("x.no_forms_yet")}</td></tr>}
          </tbody>
        </table>
      </div>
      {creating && <NewFormModal onClose={() => setCreating(false)} onSaved={(f) => { setCreating(false); load(); open(f); }} />}
      {viewing && <FormView form={viewing} canEdit={can("forms", "edit")} onClose={() => setViewing(null)} onChanged={() => { setViewing(null); load(); }} />}
    </div>
  );
}
